import { useState } from 'react'
import styled from 'styled-components'
import { useTranslation, Trans } from 'react-i18next'
import { Input } from '@/components/form'
import SignaturePreview from '@/components/signature-preview'
import { Box } from '@/components/structure'
import { Label } from '@/components/text'
import { colors } from '@/styles'
import useProfile from '@/hooks/use-profile'
import useHasScrolledToBottom from '@/hooks/use-has-scrolled-to-bottom'
import { showSignatureModal } from '@/components/signature-modal'
import { PaymentData } from '@/features/Payments/types'
import config from '@/config'
import type { ListingInfo, FlatPadInfo } from '@/features/Pad/types'
import Terms from './terms'
import { StyledPanel, CheckboxText, StyledAnchor, WarningRow } from './elements'

const { WEB_URL } = config

type AgreementFormProps = {
  handleSaveSignature?: (e: { signature: string }) => void
  isModal?: boolean
  values: FlatPadInfo
  contract: ListingInfo
  paymentData: PaymentData
  mutate: React.Dispatch<React.SetStateAction<HelperProps>>
  data: HelperProps & { count: number }
}

const CheckBox = styled(Input)`
  margin-left: 20px;
`

const CheckboxContainer = styled.div<{ isModal?: boolean; top?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;

  ${props =>
    !props.isModal &&
    `
    border-top: 1px solid ${colors.grey};
    padding-top: 24px;
  `}

  ${props =>
    props.top &&
    `
    padding-top: 16px;
  `};
`

const SignatureWrapper = styled.div`
  max-width: 325px;
  margin: 0 auto;
`

type HelperProps = {
  terms1: boolean
  terms2: boolean
  terms3: boolean
  index: number
  signature: string | null
}

export const useAgreementForm = (contracts: ListingInfo[]) => {
  const [data, mutate] = useState<HelperProps>({
    terms1: false,
    terms2: false,
    terms3: false,
    index: 0,
    signature: null
  })
  const count = contracts?.length ?? 0
  const isValid = data.terms1 && data.terms2 && !!data.signature
  return {
    data: { ...data, count },
    isValid,
    current: contracts[data.index],
    mutate
  }
}

export const AgreementForm: React.FC<AgreementFormProps> = ({
  paymentData,
  contract,
  values,
  mutate,
  data,
  isModal = false
}) => {
  const { t } = useTranslation()
  const userProfile = useProfile()
  const [showError, setShowError] = useState(false)
  const { scrollRef, hasScrolledToBottom } = useHasScrolledToBottom()
  const hasSigned = !!data.signature
  const handleSaveSignature = ({ signature }: { signature: string }) => {
    mutate(data => ({ ...data, signature }))
  }
  return (
    <StyledPanel isModal={isModal}>
      <Terms
        isModal={isModal}
        values={values}
        listingInfo={contract}
        paymentData={paymentData}
        scrollRef={scrollRef}
        hasScrolledToBottom={hasScrolledToBottom}
        showError={showError}
      />
      <CheckboxContainer isModal={isModal} top>
        <CheckboxText htmlFor="agree_to_terms">
          {t(
            'r.payment_settings.view.read_terms_conditions.label',
            'I have read the above Personal Pre-Authorized Debit Plan Terms and Conditions and understand that by signing below I agree to be bound by the Personal Pre-Authorized Debit Plan Terms and Conditions'
          )}
        </CheckboxText>
        <CheckBox
          data-cy="agree_to_terms"
          id="agree_to_terms"
          data-testid="agree-to-terms"
          type="checkbox"
          checked={data.terms1}
          hasError={!data.terms1 && showError}
          onChange={e => mutate(data => ({ ...data, terms1: e.target.checked }))}
        />
      </CheckboxContainer>
      {showError && !data.terms1 && (
        <WarningRow marginTop={0} marginBottom={0}>
          {t('common.required', 'Required')}
        </WarningRow>
      )}
      <CheckboxContainer isModal={isModal} top>
        <CheckboxText htmlFor="agree_to_terms_2">
          {t(
            'r.payment_settings.view.payee_prompton.label',
            'I understand that the Payee is not my landlord but rather is Prompton Real Estate Services Inc.. I also understand that that funds debited from my account will not be immediately disbursed to my landlord.'
          )}
        </CheckboxText>
        <CheckBox
          data-cy="agree_to_terms_2"
          id="agree_to_terms_2"
          data-testid="agree-to-terms_2"
          type="checkbox"
          checked={data.terms2}
          hasError={!data.terms2 && showError}
          onChange={e => mutate(data => ({ ...data, terms2: e.target.checked }))}
        />
      </CheckboxContainer>
      {showError && !data.terms2 && (
        <WarningRow marginTop={0} marginBottom={0}>
          {t('common.required', 'Required')}
        </WarningRow>
      )}
      <CheckboxContainer isModal top>
        <CheckboxText htmlFor="agree_to_terms_3">
          <Trans i18nKey="l.payment_settings.view.terms_privacy.label">
            I have read and agree to liv.rent's{' '}
            <StyledAnchor
              as="a"
              href={`${WEB_URL}/terms`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms & Conditions
            </StyledAnchor>{' '}
            &{' '}
            <StyledAnchor
              as="a"
              href={`${WEB_URL}/privacy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </StyledAnchor>
            .
          </Trans>
        </CheckboxText>
        <CheckBox
          data-cy="agree_to_terms_3"
          id="agree_to_terms_3"
          data-testid="agree-to-terms_3"
          type="checkbox"
          checked={data.terms3}
          hasError={!data.terms3 && showError}
          onChange={e => mutate(data => ({ ...data, terms3: e.target.checked }))}
        />
      </CheckboxContainer>
      {showError && !data.terms3 && (
        <WarningRow marginTop={0} marginBottom={0}>
          {t('common.required', 'Required')}
        </WarningRow>
      )}
      <SignatureWrapper>
        <SignaturePreview
          key={userProfile?.id}
          isCurrentUser
          toggleSignaturePad={() => {
            if (!hasScrolledToBottom || !data.terms1 || !data.terms2 || !data.terms3)
              setShowError(true)
            else {
              showSignatureModal({ onSave: handleSaveSignature })
            }
          }}
          signatures={hasSigned ? [data.signature] : []}
          renter={{
            ...userProfile,
            first_name: values.first_name,
            last_name: values.last_name
          }}
          localSignature={data.signature}
        />
      </SignatureWrapper>
      {data.count > 1 && (
        <Box color={colors.grey56}>
          <Label fontSize="14px">
            {data.index + 1} of {data.count}
          </Label>
        </Box>
      )}
    </StyledPanel>
  )
}
